import { Filter, TRule } from '../filters.types';

/**
 * Generates a query string from filters
 * Example output: "(name == 'John' AND age > 25) OR (status == 'active')"
 *
 * @param filters - Array of filter groups
 * @param topLevelJoinOperator - The join operator between filter groups (AND/OR)
 */
export const generateQueryString = (filters: Filter[], topLevelJoinOperator?: string | null): string => {
  if (!filters || filters.length === 0) {
    return 'No filters applied';
  }

  const filterStrings = filters.map((filter) => {
    if (!filter.rules || filter.rules.length === 0) {
      return '';
    }

    const ruleStrings = filter.rules
      .map((rule: TRule) => {
        if (!rule.col || !rule.operator) {
          return '';
        }

        let valueStr = '';
        if (rule.value !== null && rule.value !== undefined) {
          // Handle different value types
          if (typeof rule.value === 'string') {
            valueStr = `'${rule.value}'`;
          } else if (Array.isArray(rule.value)) {
            valueStr = `[${rule.value.map((v) => (typeof v === 'string' ? `'${v}'` : v)).join(', ')}]`;
          } else if (typeof rule.value === 'object') {
            // Handle date ranges or complex objects
            valueStr = JSON.stringify(rule.value);
          } else {
            valueStr = String(rule.value);
          }
        } else {
          valueStr = 'null';
        }

        return `${rule.col} ${rule.operator} ${valueStr}`;
      })
      .filter((str) => str !== '');

    if (ruleStrings.length === 0) {
      return '';
    }

    const joinOp = filter.operator || 'AND';
    if (ruleStrings.length === 1) {
      return ruleStrings[0];
    }

    return `(${ruleStrings.join(` ${joinOp} `)})`;
  });

  const nonEmptyFilters = filterStrings.filter((str) => str !== '');

  if (nonEmptyFilters.length === 0) {
    return 'No valid filters';
  }

  if (nonEmptyFilters.length === 1) {
    return nonEmptyFilters[0];
  }

  // Use the provided top-level join operator, or default to 'OR'
  const topLevelOperator = topLevelJoinOperator || 'OR';
  return nonEmptyFilters.join(` ${topLevelOperator} `);
};
